import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Icon } from '@iconify/react'
import { Button, Input, Tooltip, Popover, Select, Avatar } from 'antd'
import { debounce } from 'lodash'

// slices
import {
    usersState,
    resetClientModalUsers,
    setClientModalUserSearch,
    projectTaskListAssigneeUser,
} from '../../users/users.slice'
import { loginState } from '../../login/login.slice'

// assets
import { CloseModalIcon, CrossIcon } from '../../../assets/icons'

// css
import styles from './ProjectTaskToolbar.module.css'
import { fetchProjectStatusList, projectsState } from '../projects.slice'
import { useTaskListKindPermission } from '../projectPermissions'

const ProjectTaskToolbar = ({
    activeViewTab,
    onAddTask,
    onSearch,
    projectId,
    onFilterChange,
    taskListKind = 'support',
}) => {
    const dispatch = useDispatch()
    const {
        clientModalUserSearch,
        projectTaskAssigneeUser = [],
        projectTaskAssigneeUserListLoading,
        projectTaskAssigneeUserIsNext,
    } = useSelector(usersState)
    const { userProfile } = useSelector(loginState)
    const { projectStatusList, projectStatusListLoading } =
        useSelector(projectsState)
    const { canEdit: canEditModule } = useTaskListKindPermission(taskListKind)
    const [filterAssignees, setFilterAssignees] = useState([])
    const [filterStatus, setFilterStatus] = useState([])
    const [filterType, setFilterType] = useState([])
    const [quickFilters, setQuickFilters] = useState({
        incomplete: false,
        completed: false,
        mine: false,
        dueThisWeek: false,
        dueNextWeek: false,
    })
    const [filterSelectedMap, setFilterSelectedMap] = useState({})
    const [filterPopoverOpen, setFilterPopoverOpen] = useState(false)
    const [searchValue, setSearchValue] = useState('')
    const [userSearchValue, setUserSearchValue] = useState('')
    const [isSearchExpanded, setIsSearchExpanded] = useState(false)
    const searchWrapperRef = useRef(null)
    const searchInputRef = useRef(null)
    const filtersRef = useRef({
        filterAssignees: [],
        filterStatus: [],
        filterType: [],
        quickFilters: {
            incomplete: false,
            completed: false,
            mine: false,
            dueThisWeek: false,
            dueNextWeek: false,
        },
    })

    useEffect(() => {
        filtersRef.current = {
            filterAssignees,
            filterStatus,
            filterType,
            quickFilters,
        }
    }, [filterAssignees, filterStatus, filterType, quickFilters])

    // Helper functions
    const getMemberColor = (memberName) => {
        const colors = [
            '#60a5fa',
            '#34d399',
            '#a78bfa',
            '#f472b6',
            '#fb923c',
            '#fbbf24',
            '#4ade80',
            '#22d3ee',
        ]

        if (!memberName) return colors[0]

        let hash = 0
        for (let i = 0; i < memberName.length; i++) {
            hash = memberName.charCodeAt(i) + ((hash << 5) - hash)
        }

        return colors[Math.abs(hash) % colors.length]
    }

    const getInitials = (fullName) => {
        if (!fullName) return ''
        const names = fullName.trim().split(/\s+/)
        if (names.length >= 2) {
            return (
                names[0].charAt(0).toUpperCase() +
                names[names.length - 1].charAt(0).toUpperCase()
            )
        } else if (names.length === 1) {
            return names[0].charAt(0).toUpperCase()
        }
        return ''
    }

    const hexToRgba = (hex, opacity) => {
        const r = parseInt(hex.slice(1, 3), 16)
        const g = parseInt(hex.slice(3, 5), 16)
        const b = parseInt(hex.slice(5, 7), 16)
        return `rgba(${r}, ${g}, ${b}, ${opacity})`
    }

    const getTypeIcon = (typeValue) => {
        const normalized = String(typeValue || '')
            .trim()
            .toLowerCase()
        if (normalized === 'bug') return 'solar:bug-outline'
        return 'qlementine-icons:task-16'
    }

    const getTypeIconColor = (typeValue) => {
        const normalized = String(typeValue || '')
            .trim()
            .toLowerCase()
        if (normalized === 'bug') return '#e63131'
        return '#6d28d9'
    }

    const renderTypeOptionLabel = (typeValue) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Icon
                icon={getTypeIcon(typeValue)}
                width={14}
                height={14}
                style={{ color: getTypeIconColor(typeValue) }}
            />
            <span>{typeValue}</span>
        </span>
    )

    const projectStatusListOptions = useMemo(() => {
        if (projectStatusList && projectStatusList.length > 0) {
            return projectStatusList.map((status) => ({
                label: status,
                value: status,
            }))
        }
        return []
    }, [projectStatusList])
    const taskTypeOptions = useMemo(
        () => [
            { label: 'Task', value: 'Task' },
            { label: 'Bug', value: 'Bug' },
        ],
        []
    )

    const userOptions = useMemo(() => {
        const staticOption = {
            label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Avatar
                        size={28}
                        style={{ backgroundColor: '#e5e7eb', color: '#000' }}
                    >
                        U
                    </Avatar>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>
                            Unassigned
                        </span>
                        <span style={{ fontSize: 12, color: '#64748b' }}>
                            No assignee
                        </span>
                    </div>
                </div>
            ),
            value: 'UNASSIGNED', // 👈 unique constant value
            textLabel: 'Unassigned',
            isStatic: true,
        }

        if (projectTaskAssigneeUser && projectTaskAssigneeUser?.length > 0) {
            const dynamicOptions = projectTaskAssigneeUser.map((user) => ({
                label: (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        {user?.profilePicture ? (
                            <Avatar size={28} src={user.profilePicture} />
                        ) : (
                            <Avatar
                                size={28}
                                style={{
                                    backgroundColor: getMemberColor(user?.name),
                                }}
                            >
                                {getInitials(user?.name)}
                            </Avatar>
                        )}
                        <div
                            style={{ display: 'flex', flexDirection: 'column' }}
                        >
                            <span style={{ fontSize: 13, fontWeight: 500 }}>
                                {user?.name}
                            </span>
                            <span style={{ fontSize: 12, color: '#64748b' }}>
                                {user?.email}
                            </span>
                        </div>
                    </div>
                ),
                value: user?._id,
                user,
                textLabel: user?.name || user?.email || 'Unnamed User',
                profilePicture: user?.profilePicture,
            }))

            return [staticOption, ...dynamicOptions] // 👈 static on top
        }

        return [staticOption]
    }, [projectTaskAssigneeUser])

    // Fetch users when popover opens
    useEffect(() => {
        // if (filterPopoverOpen && projectId) {
        dispatch(resetClientModalUsers())
        // dispatch(
        //     usersListForClientModal({
        //         skip: 0,
        //         filter: {},
        //         limit: 100,
        //         search: '',
        //         projectId: projectId,
        //     })
        // )

        dispatch(
            projectTaskListAssigneeUser({
                skip: 0,
                limit: 100,
                search: '',
                projectId: projectId,
                ...(taskListKind === 'main' ? { type: 'pms' } : {}),
            })
        )

        dispatch(
            fetchProjectStatusList({
                skip: 0,
                filter: {},
                limit: 100,
                search: '',
                projectId: projectId,
                ...(taskListKind === 'main' ? { type: 'pms' } : {}),
            })
        )
        // }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, projectId, taskListKind])

    // Debounced search for Filter by Assignee → support/task/assignee-list
    const handleFilterUserSearch = React.useMemo(
        () =>
            debounce((value) => {
                dispatch(setClientModalUserSearch(value))
                dispatch(
                    projectTaskListAssigneeUser({
                        skip: 0,
                        limit: 100,
                        search: value || '',
                        projectId: projectId,
                        ...(taskListKind === 'main' ? { type: 'pms' } : {}),
                    })
                )
            }, 300),
        [dispatch, projectId, taskListKind]
    )

    // Load more assignee users on scroll → support/task/assignee-list
    const handleFilterUserScroll = (e) => {
        const { target } = e
        const scrollTop = target.scrollTop
        const scrollHeight = target.scrollHeight
        const clientHeight = target.clientHeight

        if (
            scrollHeight - scrollTop <= clientHeight + 50 &&
            projectTaskAssigneeUserIsNext &&
            projectTaskAssigneeUserListLoading !== 'loading'
        ) {
            const skip = projectTaskAssigneeUser?.length || 0

            dispatch(
                projectTaskListAssigneeUser({
                    skip,
                    limit: 100,
                    search: clientModalUserSearch || '',
                    projectId: projectId,
                    ...(taskListKind === 'main' ? { type: 'pms' } : {}),
                })
            )
        }
    }

    // Handle filter assignee change
    const emitFilterChange = (
        selectedAssigneesValue = filterAssignees,
        selectedStatusValue = filterStatus,
        selectedTypeValue = filterType,
        quickFiltersValue = quickFilters,
        searchTerm = searchValue
    ) => {
        const loggedInUserId =
            userProfile?._id || userProfile?.id || userProfile?.userId
        const isCompleted =
            quickFiltersValue.completed && !quickFiltersValue.incomplete
                ? true
                : quickFiltersValue.incomplete && !quickFiltersValue.completed
                  ? false
                  : undefined

        onFilterChange?.({
            selectedAssignees: selectedAssigneesValue,
            selectedStatus: selectedStatusValue,
            ...(taskListKind === 'main'
                ? {
                      selectedType: selectedTypeValue,
                      // Always send type so clearing (cross / clear-all) overwrites
                      // the previous value in the parent's merged toolbar filter.
                      type:
                          selectedTypeValue.length === 1
                              ? selectedTypeValue[0]
                              : selectedTypeValue.length > 1
                                ? selectedTypeValue
                                : null,
                  }
                : {}),
            quickFilters: quickFiltersValue,
            search: searchTerm,
            isCompleted:
                typeof isCompleted === 'boolean' ? isCompleted : null,
            mineAssignee:
                quickFiltersValue.mine && loggedInUserId
                    ? [loggedInUserId]
                    : null,
        })
    }

    const handleQuickFilterToggle = (key) => {
        setQuickFilters((prev) => {
            const toggledValue = !prev[key]
            const next = {
                incomplete: false,
                completed: false,
                mine: false,
                dueThisWeek: false,
                dueNextWeek: false,
            }
            if (toggledValue) {
                next[key] = true
            }
            emitFilterChange(filterAssignees, filterStatus, filterType, next)
            return next
        })
    }

    // Handle filter assignee change
    const handleFilterAssigneeChange = (selectedValues, selectedOptions) => {
        setUserSearchValue('')
        handleFilterUserSearch('')
        setFilterAssignees(selectedValues)
        const updatedMap = { ...filterSelectedMap }
        if (selectedOptions && selectedOptions.length > 0) {
            selectedOptions.forEach((sel) => {
                if (!updatedMap[sel.value]) {
                    const match = userOptions.find(
                        (opt) => opt.value === sel.value
                    )
                    if (match) updatedMap[sel.value] = match
                }
            })
        } else {
            // Fallback: find from userOptions based on selectedValues
            selectedValues.forEach((val) => {
                if (!updatedMap[val]) {
                    const match = userOptions.find((opt) => opt.value === val)
                    if (match) updatedMap[val] = match
                }
            })
        }
        // Remove unselected items
        Object.keys(updatedMap).forEach((key) => {
            if (!selectedValues.includes(key)) {
                delete updatedMap[key]
            }
        })
        setFilterSelectedMap(updatedMap)
        // Notify parent component about filter change
        emitFilterChange(selectedValues, filterStatus, filterType, quickFilters)
    }

    const handleFilterStatus = (selectedStatus) => {
        setFilterStatus(selectedStatus)
        emitFilterChange(
            filterAssignees,
            selectedStatus,
            filterType,
            quickFilters
        )
    }

    const handleFilterType = (selectedType) => {
        setFilterType(selectedType)
        emitFilterChange(
            filterAssignees,
            filterStatus,
            selectedType,
            quickFilters
        )
    }

    // Clear filter
    const handleClearFilter = () => {
        setFilterAssignees([])
        setFilterSelectedMap({})
        setFilterStatus([])
        setFilterType([])
        const clearedQuick = {
            incomplete: false,
            completed: false,
            mine: false,
            dueThisWeek: false,
            dueNextWeek: false,
        }
        setQuickFilters(clearedQuick)
        emitFilterChange([], [], [], clearedQuick)
    }

    // Store latest onSearch callback
    const onSearchRef = useRef(onSearch)
    const onFilterChangeRef = useRef(onFilterChange)

    useEffect(() => {
        onSearchRef.current = onSearch
    }, [onSearch])

    useEffect(() => {
        onFilterChangeRef.current = onFilterChange
    }, [onFilterChange])

    const emitFilterChangeFromRefs = useCallback(
        (searchTerm) => {
            const {
                filterAssignees: assignees,
                filterStatus: status,
                filterType: type,
                quickFilters: quick,
            } = filtersRef.current
            const loggedInUserId =
                userProfile?._id || userProfile?.id || userProfile?.userId
            const isCompleted =
                quick.completed && !quick.incomplete
                    ? true
                    : quick.incomplete && !quick.completed
                      ? false
                      : undefined

            onFilterChangeRef.current?.({
                selectedAssignees: assignees,
                selectedStatus: status,
                ...(taskListKind === 'main'
                    ? {
                          selectedType: type,
                          type:
                              type.length === 1
                                  ? type[0]
                                  : type.length > 1
                                    ? type
                                    : null,
                      }
                    : {}),
                quickFilters: quick,
                search: searchTerm,
                isCompleted:
                    typeof isCompleted === 'boolean' ? isCompleted : null,
                mineAssignee:
                    quick.mine && loggedInUserId ? [loggedInUserId] : null,
            })
        },
        [userProfile, taskListKind]
    )

    // Create stable debounced function
    const debouncedSearch = React.useMemo(
        () =>
            debounce((value) => {
                if (taskListKind === 'main') {
                    emitFilterChangeFromRefs(value)
                    return
                }
                onSearchRef.current?.(value)
            }, 500),
        [taskListKind, emitFilterChangeFromRefs]
    )

    const handleSearch = (value) => {
        setSearchValue(value)
        debouncedSearch(value)
    }

    const handleAddTaskClick = () => {
        onAddTask?.()
    }

    const handleSearchIconClick = () => {
        setIsSearchExpanded(true)
        setTimeout(() => {
            if (searchInputRef.current) {
                const inputElement =
                    searchInputRef.current.input || searchInputRef.current
                if (inputElement && inputElement.focus) {
                    inputElement.focus()
                }
            }
        }, 150)
    }

    // Handle click outside to collapse search
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                searchWrapperRef.current &&
                !searchWrapperRef.current.contains(event.target) &&
                isSearchExpanded
            ) {
                if (!searchValue.trim()) {
                    setIsSearchExpanded(false)
                }
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isSearchExpanded, searchValue])

    const totalQuickFilters = Object.values(quickFilters).filter(Boolean).length

    const totalFilters =
        (filterAssignees?.length || 0) +
        (filterStatus?.length || 0) +
        (taskListKind === 'main' ? filterType?.length || 0 : 0) +
        totalQuickFilters

    const renderQuickFilterChip = (key, label, icon, trailingIcon = null) => {
        const isActive = !!quickFilters[key]
        return (
            <button
                type="button"
                className={`${styles.quickFilterChip} ${
                    isActive ? styles.quickFilterChipActive : ''
                }`}
                onClick={() => handleQuickFilterToggle(key)}
            >
                {isActive ? (
                    <Icon icon="mdi:check" width={14} />
                ) : (
                    <Icon icon={icon} width={14} />
                )}
                {label}
                {!isActive && trailingIcon ? (
                    <Icon icon={trailingIcon} width={14} />
                ) : null}
            </button>
        )
    }

    const isUnassignedSelected =
        Array.isArray(filterAssignees) && filterAssignees.includes('UNASSIGNED')

    const isOtherSelected = filterAssignees?.some((v) => v !== 'UNASSIGNED')

    // Shared filter popover content (assignee + status sections)
    const filterPopoverContent = (
        <>
            {taskListKind === 'support' && (
                <div className={styles.quickFiltersSection}>
                    <div className={styles.quickFiltersHeader}>
                        <div className={styles.quickFiltersTitle}>
                            Quick filters
                        </div>
                        <button
                            type="button"
                            className={`${styles.quickFiltersClear} ${
                                totalFilters === 0
                                    ? styles.quickFiltersClearDisabled
                                    : ''
                            }`}
                            disabled={totalFilters === 0}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                if (totalFilters === 0) return
                                handleClearFilter()
                                setFilterPopoverOpen(true)
                            }}
                        >
                            Clear
                        </button>
                    </div>
                    <div className={styles.quickFiltersRow}>
                        {renderQuickFilterChip(
                            'incomplete',
                            'Incomplete tasks',
                            'mdi:check-circle-outline'
                        )}
                        {renderQuickFilterChip(
                            'completed',
                            'Completed tasks',
                            'mdi:check-circle-outline'
                        )}
                        {renderQuickFilterChip(
                            'mine',
                            'Just my tasks',
                            'mdi:account-outline'
                        )}
                    </div>
                </div>
            )}

            {/* Filter by Assignee */}
            <div style={{ width: 300, padding: '8px 0' }}>
                <div
                    style={{
                        marginBottom: 12,
                        fontSize: 13,
                        fontWeight: 500,
                        color: '#1e293b',
                    }}
                >
                    Filter by Assignee
                </div>
                <Select
                    mode="multiple"
                    value={filterAssignees}
                    removeIcon={
                        <div style={{ height: '14px', width: '14px' }}>
                            <CloseModalIcon />
                        </div>
                    }
                    open={isUnassignedSelected ? false : undefined}
                    placeholder="Select assignees..."
                    size="small"
                    filterOption={false}
                    searchValue={userSearchValue}
                    onSearch={(value) => {
                        if (isUnassignedSelected) return
                        setUserSearchValue(value)
                        handleFilterUserSearch(value)
                    }}
                    autoClearSearchValue={false}
                    onPopupScroll={handleFilterUserScroll}
                    notFoundContent={
                        projectTaskAssigneeUserListLoading === 'loading' ? (
                            <Icon
                                icon="line-md:loading-loop"
                                style={{ fontSize: 24, color: '#6366f1' }}
                            />
                        ) : null
                    }
                    loading={
                        projectTaskAssigneeUserListLoading === 'loading'
                    }
                    onChange={handleFilterAssigneeChange}
                    tagRender={(props) => {
                        const { closable, onClose, value: val } = props
                        const selectedUser = filterSelectedMap[val]
                        const memberColor = getMemberColor(
                            selectedUser?.textLabel
                        )
                        const initialsBgColor = hexToRgba(memberColor, 0.3)
                        const initials = getInitials(selectedUser?.textLabel)

                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor:
                                        'rgba(147, 147, 147, 0.15)',
                                    borderRadius: 5,
                                    padding: '1px 5px 1px 1px',
                                    margin: 0,
                                    maxWidth: '100%',
                                    border: 'none',
                                    height: '24px',
                                    lineHeight: '22px',
                                }}
                            >
                                {selectedUser?.profilePicture ? (
                                    <Avatar
                                        size={18}
                                        src={selectedUser.profilePicture}
                                        style={{
                                            marginRight: 5,
                                            borderRadius: 3,
                                        }}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            width: 18,
                                            height: 18,
                                            borderRadius: 3,
                                            backgroundColor: initialsBgColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginRight: 5,
                                            fontSize: 9,
                                            fontWeight: 600,
                                            color: '#1e293b',
                                        }}
                                    >
                                        {initials}
                                    </div>
                                )}
                                <span
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 500,
                                        color: '#1e293b',
                                        marginRight: 3,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: 90,
                                        lineHeight: '18px',
                                    }}
                                >
                                    {selectedUser?.textLabel}
                                </span>
                                {closable && (
                                    <span
                                        onClick={onClose}
                                        style={{
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginLeft: 2,
                                            padding: '1px',
                                        }}
                                    >
                                        <CrossIcon width={8} color="#64748b" />
                                    </span>
                                )}
                            </div>
                        )
                    }}
                    options={userOptions.map((opt) => ({
                        ...opt,
                        disabled:
                            opt.value === 'UNASSIGNED'
                                ? isOtherSelected
                                : isUnassignedSelected,
                    }))}
                    className={styles.filterSelect}
                    popupRender={(menu) => (
                        <>
                            {menu}
                            {projectTaskAssigneeUserListLoading ===
                                'loading' &&
                                (projectTaskAssigneeUser?.length || 0) > 0 && (
                                    <div
                                        style={{
                                            padding: '8px',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <Icon
                                            icon="line-md:loading-loop"
                                            style={{
                                                fontSize: 24,
                                                color: '#6366f1',
                                            }}
                                        />
                                    </div>
                                )}
                        </>
                    )}
                />
            </div>

            {/* Filter by Status */}
            <div style={{ width: 300, padding: '8px 0' }}>
                <div
                    style={{
                        marginBottom: 12,
                        fontSize: 13,
                        fontWeight: 500,
                        color: '#1e293b',
                    }}
                >
                    Filter by Status
                </div>
                <Select
                    mode="multiple"
                    value={filterStatus}
                    removeIcon={
                        <div style={{ height: '14px', width: '14px' }}>
                            <CloseModalIcon />
                        </div>
                    }
                    placeholder="Select Status..."
                    size="small"
                    filterOption={false}
                    disabled={projectStatusListLoading}
                    loading={projectStatusListLoading}
                    onChange={handleFilterStatus}
                    tagRender={(props) => {
                        const { closable, onClose, value: val } = props
                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor:
                                        'rgba(147, 147, 147, 0.15)',
                                    borderRadius: 5,
                                    padding: '1px 5px 1px 1px',
                                    margin: 0,
                                    maxWidth: '100%',
                                    border: 'none',
                                    height: '24px',
                                    lineHeight: '22px',
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 500,
                                        color: '#1e293b',
                                        marginRight: 3,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: 90,
                                        lineHeight: '18px',
                                    }}
                                >
                                    {val}
                                </span>
                                {closable && (
                                    <span
                                        onClick={onClose}
                                        style={{
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginLeft: 2,
                                            padding: '1px',
                                        }}
                                    >
                                        <CrossIcon width={8} color="#64748b" />
                                    </span>
                                )}
                            </div>
                        )
                    }}
                    options={projectStatusListOptions}
                    className={styles.filterSelect}
                    dropdownRender={(menu) => <>{menu}</>}
                />
            </div>

            {taskListKind === 'main' && (
                <div style={{ width: 300, padding: '8px 0' }}>
                    <div
                        style={{
                            marginBottom: 12,
                            fontSize: 13,
                            fontWeight: 500,
                            color: '#1e293b',
                        }}
                    >
                        Filter by Type
                    </div>
                    <Select
                        mode="multiple"
                        value={filterType}
                        removeIcon={
                            <div style={{ height: '14px', width: '14px' }}>
                                <CloseModalIcon />
                            </div>
                        }
                        placeholder="Select Type..."
                        size="small"
                        filterOption={false}
                        onChange={handleFilterType}
                        optionRender={(option) =>
                            renderTypeOptionLabel(option.value)
                        }
                        tagRender={(props) => {
                            const { closable, onClose, value: val } = props
                            return (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor:
                                            'rgba(147, 147, 147, 0.15)',
                                        borderRadius: 5,
                                        padding: '1px 5px 1px 3px',
                                        margin: 0,
                                        maxWidth: '100%',
                                        border: 'none',
                                        height: '24px',
                                        lineHeight: '22px',
                                    }}
                                >
                                    <Icon
                                        icon={getTypeIcon(val)}
                                        width={12}
                                        height={12}
                                        style={{
                                            color: getTypeIconColor(val),
                                            marginRight: 4,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <span
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 500,
                                            color: '#1e293b',
                                            marginRight: 3,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: 90,
                                            lineHeight: '18px',
                                        }}
                                    >
                                        {val}
                                    </span>
                                    {closable && (
                                        <span
                                            onClick={onClose}
                                            style={{
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                marginLeft: 2,
                                                padding: '1px',
                                            }}
                                        >
                                            <CrossIcon
                                                width={8}
                                                color="#64748b"
                                            />
                                        </span>
                                    )}
                                </div>
                            )
                        }}
                        options={taskTypeOptions}
                        className={styles.filterSelect}
                    />
                </div>
            )}
        </>
    )

    const hasActiveFilters = totalFilters > 0

    return (
        <div
            className={styles.toolbar}
            style={{
                borderBottom: 'none',
                display: 'flex',
                justifyContent: 'end',
            }}
        >
            <div
                className={
                    hasActiveFilters ? styles.filterBadge : undefined
                }
            >
                <Popover
                    arrow={false}
                    content={filterPopoverContent}
                    title={null}
                    trigger="click"
                    open={filterPopoverOpen}
                    onOpenChange={setFilterPopoverOpen}
                    placement="bottomRight"
                >
                    {hasActiveFilters ? (
                        <div className={styles.filterBadgeContent}>
                            <Icon
                                icon="mynaui:filter"
                                width={16}
                                height={16}
                                className={styles.filterBadgeIcon}
                            />
                            <span className={styles.filterBadgeText}>
                                Filters: {totalFilters}
                            </span>
                        </div>
                    ) : (
                        <Button
                            type="text"
                            className={styles.toolbarButton}
                            icon={
                                <Icon
                                    icon="mynaui:filter"
                                    width={18}
                                    height={18}
                                />
                            }
                        >
                            Filter
                        </Button>
                    )}
                </Popover>
                {hasActiveFilters ? (
                    <button
                        className={styles.filterBadgeClose}
                        onClick={handleClearFilter}
                        type="button"
                    >
                        <Icon
                            icon="mdi:close"
                            width={14}
                            height={14}
                            className={styles.filterBadgeCloseIcon}
                        />
                    </button>
                ) : null}
            </div>
            {canEditModule &&
                activeViewTab !== 'roadmap' &&
                activeViewTab !== 'sprint' && (
                    <Button
                        type="default"
                        className={styles.addTaskButton}
                        onClick={handleAddTaskClick}
                        icon={<Icon icon="mdi:plus" width={16} height={16} />}
                    >
                        Add Task
                    </Button>
                )}

            <div
                ref={searchWrapperRef}
                className={`${styles.searchWrapper} ${
                    isSearchExpanded ? styles.searchExpanded : ''
                }`}
            >
                {!isSearchExpanded ? (
                    <Tooltip title="Search this view">
                        <div
                            className={styles.searchIconButton}
                            onClick={handleSearchIconClick}
                        >
                            <Icon
                                icon="mdi:magnify"
                                width={18}
                                height={18}
                                className={styles.searchIcon}
                            />
                        </div>
                    </Tooltip>
                ) : (
                    <Input
                        ref={searchInputRef}
                        placeholder={
                            activeViewTab === 'sprint'
                                ? 'Search task names and epics'
                                : 'Search task names'
                        }
                        allowClear
                        value={searchValue}
                        onChange={(e) => handleSearch(e.target.value)}
                        onPressEnter={(e) => handleSearch(e.target.value)}
                        className={styles.searchInput}
                        prefix={
                            <Icon
                                icon="mdi:magnify"
                                width={18}
                                height={18}
                                className={styles.searchIcon}
                            />
                        }
                        onBlur={(e) => {
                            if (!searchValue.trim()) {
                                // Don't collapse if there's a value
                            }
                        }}
                    />
                )}
            </div>
        </div>
    )
}

export default ProjectTaskToolbar
